import { formatDate } from "@/utils/date-time-utility";
import { replaceUnderScoreAddSpace } from "@/utils/utils";
import {
  NOTE_PREVIEW_LABELS,
  NOTE_PREVIEW_SCREENING_SECTION_KEY,
} from "./note-preview-constant";
import { isFreeTextTitle } from "./note-preview-line-utils";
import {
  NotePreviewLine,
  NotePreviewScreeningResult,
  NotePreviewSection,
} from "./note-preview-types";

/** "AAQ_II", "aaq ii" and "AAQ II" all name the same screener. */
export const normaliseScreenerKey = (type: string): string =>
  type
    .replace(/[\s_-]+/g, " ")
    .trim()
    .toLowerCase();

const titleCase = (text: string): string =>
  text
    .toLowerCase()
    .split(" ")
    .map((word) => (word ? word[0].toUpperCase() + word.slice(1) : word))
    .join(" ");

/** "Score 4 (None Minimal) – 10/26/2026", matching the note screen. */
export const formatScreeningResult = (
  result: NotePreviewScreeningResult
): string => {
  const severity = titleCase(replaceUnderScoreAddSpace(result.severity));
  const score = `${NOTE_PREVIEW_LABELS.scorePrefix}${result.score}`;
  const withSeverity = severity ? `${score} (${severity})` : score;
  const date = result.createdAt ? formatDate(result.createdAt) : "";
  return date
    ? `${withSeverity}${NOTE_PREVIEW_LABELS.screeningDateSeparator}${date}`
    : withSeverity;
};

const enrichLine = (
  line: NotePreviewLine,
  results: Record<string, NotePreviewScreeningResult>
): NotePreviewLine => {
  if (!line.title || isFreeTextTitle(line.title)) return line;
  const result = results[normaliseScreenerKey(line.title)];
  return result ? { ...line, text: formatScreeningResult(result) } : line;
};

/** Replaces the backend's screener summary with score, severity and date from the screening records. */
export const enrichScreeningSections = (
  sections: NotePreviewSection[],
  results: Record<string, NotePreviewScreeningResult>
): NotePreviewSection[] =>
  Object.keys(results).length
    ? sections.map((section) =>
        section.key === NOTE_PREVIEW_SCREENING_SECTION_KEY
          ? {
              ...section,
              lines: section.lines.map((line) => enrichLine(line, results)),
            }
          : section
      )
    : sections;
